import { useEffect, useState } from 'react'
import { api } from '../api.js'
import { useAuth } from '../context/auth.jsx'
import { PRESET_THEMES, useTheme } from '../context/theme.jsx'

const S = {
  page: { maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 16 },
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-sm)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 },
  lbl: { display: 'block', fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 },
  inp: { width: '100%', boxSizing: 'border-box', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 9, padding: '10px 12px', fontSize: 13, color: 'var(--text)', outline: 'none' },
  btn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, border: '1px solid var(--border)', borderRadius: 9, padding: '9px 14px', fontSize: 13, fontWeight: 750, cursor: 'pointer', background: 'var(--accent)', color: 'var(--accent-contrast)' },
  ghost: { background: 'var(--surface-muted)', color: 'var(--text)' },
}
function initials(name) { const p = String(name || 'U').trim().split(/\s+/); return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || 'U' }
export default function Profile() {
  const { user } = useAuth()
  const { theme, preset, toggle, cyclePreset, selectPreset, resetPreset } = useTheme()
  const [form, setForm] = useState({ name: '', email: '', password: '' })
  const [savedPassword, setSavedPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadingPass, setLoadingPass] = useState(false)
  const [msg, setMsg] = useState('')
  const [error, setError] = useState('')
  useEffect(() => { setForm({ name: user?.name || '', email: user?.email || '', password: '' }) }, [user])
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const revealPassword = async () => {
    if (!user?.id) return
    if (savedPassword) { setShowPassword((v) => !v); return }
    setLoadingPass(true); setError('')
    try { const r = await api(`/api/users/${user.id}/password`); setSavedPassword(r.password || ''); setShowPassword(true) }
    catch (e) { setError(e.message || 'Could not load saved password') }
    finally { setLoadingPass(false) }
  }
  const save = async () => {
    if (!user?.id) return
    if (!form.name || !form.email) { setError('Name and email are required'); return }
    setSaving(true); setMsg(''); setError('')
    try {
      const body = { name: form.name, email: form.email, role: user.role }
      if (form.password.trim()) body.password = form.password.trim()
      await api(`/api/users/${user.id}`, { method: 'PUT', body })
      setMsg('Profile saved.')
      if (form.password.trim()) { setSavedPassword(form.password.trim()); setShowPassword(true); setForm((f) => ({ ...f, password: '' })) }
    } catch (e) { setError(e.message || 'Could not save profile') }
    finally { setSaving(false) }
  }
  return <div style={S.page}>
    <div style={{ display:'flex', alignItems:'center', gap:12 }}><div style={{ width:48, height:48, borderRadius:15, display:'grid', placeItems:'center', background:'var(--accent-grad)', color:'var(--accent-contrast)', fontWeight:850 }}>{initials(form.name)}</div><div><div style={{ fontSize:18, fontWeight:850, color:'var(--text)' }}>My Profile</div><div style={{ fontSize:12, color:'var(--muted)', textTransform:'capitalize' }}>{user?.role || ''}</div></div></div>
    {error && <div style={{ padding:'10px 12px', borderRadius:9, color:'var(--risk)', background:'var(--risk-soft)', fontSize:13 }}>{error}</div>}
    {msg && <div style={{ padding:'10px 12px', borderRadius:9, color:'var(--positive)', background:'var(--positive-soft)', fontSize:13 }}>{msg}</div>}
    <div style={S.card}><div style={S.grid}>
      <label><span style={S.lbl}>Name</span><input style={S.inp} value={form.name} onChange={set('name')} /></label>
      <label><span style={S.lbl}>Email</span><input style={S.inp} type="email" value={form.email} onChange={set('email')} /></label>
      <label><span style={S.lbl}>New Password</span><input style={S.inp} type="text" value={form.password} onChange={set('password')} placeholder="Leave blank to keep current" /></label>
      <label><span style={S.lbl}>Saved Password</span><div style={{ display:'flex', gap:8 }}><input style={{ ...S.inp, flex:1 }} readOnly value={showPassword ? (savedPassword || '') : '••••••••'} /><button type="button" onClick={revealPassword} style={{ ...S.btn, ...S.ghost, whiteSpace:'nowrap' }}>{loadingPass ? '...' : showPassword ? 'Hide' : 'View'}</button></div></label>
    </div><div style={{ display:'flex', justifyContent:'flex-end', marginTop:18 }}><button type="button" onClick={save} disabled={saving} style={{ ...S.btn, opacity:saving ? .65 : 1 }}>{saving ? 'Saving...' : 'Save Profile'}</button></div></div>

    <div style={S.card}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:14, flexWrap:'wrap', marginBottom:16 }}>
        <div>
          <div style={{ fontSize:16, fontWeight:850, color:'var(--text)' }}>Theme & Colors</div>
          <div style={{ fontSize:12, color:'var(--muted)', marginTop:4 }}>Choose any theme color for your own panel.</div>
        </div>
        <div style={{ width:46, height:46, borderRadius:14, background:'var(--accent-grad)', border:'1px solid var(--border)', boxShadow:'var(--accent-glow)' }} title="Current theme color" />
      </div>

      <label style={{ display:'block', marginBottom:14 }}>
        <span style={S.lbl}>Theme Color</span>
        <select
          style={{ ...S.inp, cursor:'pointer' }}
          value={preset?.index ?? ''}
          onChange={(e) => e.target.value === '' ? resetPreset() : selectPreset(Number(e.target.value))}
        >
          <option value="">Default {theme === 'dark' ? 'Dark' : 'Light'} Theme</option>
          {PRESET_THEMES.map((item, index) => (
            <option key={index} value={index}>{item.name}</option>
          ))}
        </select>
      </label>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(155px, 1fr))', gap:10 }}>
        <button type="button" onClick={cyclePreset} style={S.btn} title="Same action as the header theme icon">Next Theme Color</button>
        <button type="button" onClick={toggle} style={{ ...S.btn, ...S.ghost }}>{theme === 'dark' ? 'Switch to Light' : 'Switch to Dark'}</button>
        <button type="button" onClick={resetPreset} style={{ ...S.btn, ...S.ghost }}>Reset Color</button>
      </div>

      <div style={{ marginTop:14, padding:'10px 12px', borderRadius:10, background:'var(--surface-muted)', border:'1px solid var(--border)', color:'var(--muted)', fontSize:12 }}>
        Current: <strong style={{ color:'var(--text)' }}>{preset?.name || `Default ${theme === 'dark' ? 'Dark' : 'Light'}`}</strong>
      </div>
    </div>
  </div>
}
